import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft, User, Mail, CreditCard, Crown, Calendar, 
  ExternalLink, Shield, Clock, History 
} from 'lucide-react'
import { useAdminUsers } from '../../hooks/useAdminUsers'
import { CreditAdjustModal, PlanOverrideModal } from '../../components/Admin'
import { Button, Card } from '../../components/UI'

const planColors = {
  free: 'text-dark-400 bg-dark-700',
  basic: 'text-blue-400 bg-blue-500/20',
  pro: 'text-accent-400 bg-accent-500/20',
}

const UserDetailView = () => {
  const { userId } = useParams()
  const { fetchUser, adjustCredits, overridePlan } = useAdminUsers()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showPlanModal, setShowPlanModal] = useState(false)

  useEffect(() => {
    loadUser()
  }, [userId])

  const loadUser = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchUser(userId)
      setUser(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreditAdjust = async (amount, reason) => {
    await adjustCredits(userId, amount, reason)
    loadUser() // Refresh user data
  }

  const handlePlanOverride = async (plan, note) => {
    await overridePlan(userId, plan, note)
    loadUser() // Refresh user data
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-500"></div>
        </div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="p-8">
        <Link to="/admin/users" className="inline-flex items-center gap-2 text-dark-400 hover:text-dark-200 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </Link>
        <Card className="text-center py-12">
          <p className="text-red-400">{error || 'User not found'}</p>
        </Card>
      </div>
    )
  }

  const { monetization, creditHistory } = user

  return (
    <div className="p-8">
      {/* Back Link */}
      <Link to="/admin/users" className="inline-flex items-center gap-2 text-dark-400 hover:text-dark-200 mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </Link>

      {/* User Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          {user.photoURL ? (
            <img src={user.photoURL} alt="" className="w-16 h-16 rounded-full" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-dark-700 flex items-center justify-center">
              <User className="w-8 h-8 text-dark-400" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{user.displayName || 'No name'}</h1>
            <p className="text-dark-400">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setShowCreditModal(true)}>
            <CreditCard className="w-4 h-4" />
            Adjust Credits
          </Button>
          <Button variant="ghost" onClick={() => setShowPlanModal(true)}>
            <Crown className="w-4 h-4" />
            Override Plan
          </Button>
          {monetization.stripeCustomerId && (
            <a
              href={`https://dashboard.stripe.com/customers/${monetization.stripeCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline">
                <ExternalLink className="w-4 h-4" />
                Stripe
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* Account Info */}
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-dark-400" />
            Account
          </h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-dark-400">Email Verified</dt>
              <dd>{user.emailVerified ? 'Yes' : 'No'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-dark-400">Joined</dt>
              <dd>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-dark-400">Last Sign In</dt>
              <dd>{user.lastSignIn ? new Date(user.lastSignIn).toLocaleDateString() : '-'}</dd>
            </div>
          </dl>
        </Card>

        {/* Subscription Info */}
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-dark-400" />
            Subscription
          </h3>
          <div className="mb-4">
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${planColors[monetization.plan] || planColors.free}`}>
              {monetization.plan === 'pro' && <Crown className="w-3 h-3" />}
              {monetization.plan.charAt(0).toUpperCase() + monetization.plan.slice(1)}
            </span>
            {monetization.planSource === 'admin_override' && (
              <span className="ml-2 text-xs text-yellow-500 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Admin Override
              </span>
            )}
          </div>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-dark-400">Status</dt>
              <dd className="capitalize">{monetization.subscriptionStatus || 'None'}</dd>
            </div>
            {monetization.subscriptionRenewalDate && (
              <div className="flex justify-between">
                <dt className="text-dark-400">Renews</dt>
                <dd>{new Date(monetization.subscriptionRenewalDate).toLocaleDateString()}</dd>
              </div>
            )}
            {monetization.overrideNote && (
              <div className="pt-2 border-t border-dark-700">
                <dt className="text-dark-400 mb-1">Override Note</dt>
                <dd className="text-xs text-dark-300">{monetization.overrideNote}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Credits Info */}
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-dark-400" />
            Credits
          </h3>
          <div className="text-3xl font-bold mb-4">
            {monetization.creditsBalance.toLocaleString()}
          </div>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-dark-400">Monthly Allotted</dt>
              <dd>{monetization.creditsAllottedMonthly.toLocaleString()}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-dark-400">Used This Month</dt>
              <dd>{monetization.creditsUsedThisMonth.toLocaleString()}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-dark-400">Purchased</dt>
              <dd>{monetization.creditsPurchased.toLocaleString()}</dd>
            </div>
          </dl>
        </Card>
      </div>

      {/* Credit History */}
      <Card padding="lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-dark-400" />
          Credit History
        </h3>
        {creditHistory.length === 0 ? (
          <p className="text-dark-400 text-center py-8">No credit history</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 text-dark-400 font-medium">Date</th>
                  <th className="text-left py-2 text-dark-400 font-medium">Type</th>
                  <th className="text-left py-2 text-dark-400 font-medium">Description</th>
                  <th className="text-right py-2 text-dark-400 font-medium">Amount</th>
                  <th className="text-right py-2 text-dark-400 font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {creditHistory.slice(0, 20).map((entry) => (
                  <tr key={entry.id} className="border-b border-dark-800">
                    <td className="py-2 text-dark-300">
                      {entry.date ? new Date(entry.date).toLocaleString() : '-'}
                    </td>
                    <td className="py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                        entry.type.includes('credit') || entry.type.includes('reset') || entry.type.includes('purchase')
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {entry.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2 text-dark-300 max-w-[200px] truncate">
                      {entry.description}
                    </td>
                    <td className={`py-2 text-right font-mono ${entry.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {entry.amount > 0 ? '+' : ''}{entry.amount.toLocaleString()}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {entry.balanceAfter?.toLocaleString() || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modals */}
      <CreditAdjustModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSubmit={handleCreditAdjust}
        userName={user.displayName || user.email}
        currentBalance={monetization.creditsBalance}
      />

      <PlanOverrideModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onSubmit={handlePlanOverride}
        userName={user.displayName || user.email}
        currentPlan={monetization.plan}
      />
    </div>
  )
}

export default UserDetailView
